import { KeyboardArrowDown } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import useGlobalStore from '../../store/useGlobalStore';
import { formatNumberAsWhole, handlePreventKeyDownDecimal, truncateToFixed } from '../../utility/helpers/commonFunction';
import { DISCOUNT_SOURCE } from '../../utility/helpers/discountCalculation';
import { NumericEngine, Raw } from '../../utility/helpers/numericEngine';
import PaymentDetailSvg from '../../utility/Svg/PaymentDetailSvg';
import MultiTaxRows from './MultiTaxRows';

const PaymentDetailsAccrodian = ({
    type,

    index,
    accordianExpanded,
    handleAccordianToggle,
    errors,
    control,
    customerInvoiceTaxes,
    paymentDetails,
    setPaymentDetails,
    discountId = null,
    setDiscountId,
    setSelectedDiscount,
    discountList = [],
    hideFees = false,
    /** Labour/Parts/Service Packages block, divider; Subtotal regular weight when structured; bold Total (NewInvoiceV4 / NewEstimatesV4 only) */
    structuredPaymentSummary = false,
    taxPercentageInputRef,
    isNewTaxVersion = false,
    taxesBreakdown = [],
    setTaxesBreakdown,
    documentType = 'invoice',
    lineTaxSummary = [],
    onBulkTaxToggle,
}) => {
    const [discountError, setDiscountError] = useState(false);
    const { markSaved, isDirty } = useGlobalStore();

    // Local raw strings for the custom-discount inputs. Kept separate from paymentDetails so an
    // in-progress decimal ("5." → numeric 5) isn't clobbered when the recompute normalizes the value.
    // Sync from paymentDetails only when the NUMERIC value actually differs (not just formatting).
    const [pctInput, setPctInput] = useState(truncateToFixed(paymentDetails?.discountPercentage));
    const [amtInput, setAmtInput] = useState(truncateToFixed(paymentDetails?.discount));

    useEffect(() => {
        if (Number(paymentDetails?.discountPercentage || 0) !== Number(pctInput || 0)) {
            setPctInput(truncateToFixed(paymentDetails?.discountPercentage));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentDetails?.discountPercentage]);

    useEffect(() => {
        if (Number(paymentDetails?.discount || 0) !== Number(amtInput || 0)) {
            setAmtInput(truncateToFixed(paymentDetails?.discount));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentDetails?.discount]);

    // Fix handleDiscountChange function
    const handleDiscountChange = (value) => {
        const feesAmount = Number(paymentDetails?.fees) || 0;

        // Handle "none" selection - reset discount
        if (value.target.value === 'none') {
            setDiscountId('none');
            setSelectedDiscount(null);

            // Use 0 for discount when calculating taxable amount (not the old discount value)
            const taxableAmount = NumericEngine.subtract(NumericEngine.add(paymentDetails.subTotal, feesAmount), 0);

            if (isNewTaxVersion) {
                setPaymentDetails({
                    ...paymentDetails,
                    discount: 0,
                    discountPercentage: 0,
                    discountSource: DISCOUNT_SOURCE.PERCENTAGE,
                });
                setDiscountError(false);
                return;
            }

            // Recalculate tax with no discount
            const taxPercentage = Number(paymentDetails?.taxPercentage) || 0;
            const tax = taxPercentage > 0 ? NumericEngine.multiply(NumericEngine.divide(taxPercentage, 100), taxableAmount) : '0';

            // Calculate total
            const total = NumericEngine.add(taxableAmount, tax);

            setPaymentDetails({
                ...paymentDetails,
                discount: 0,
                discountPercentage: 0,
                discountSource: DISCOUNT_SOURCE.PERCENTAGE,
                tax: Number(tax),
                total: Number(total),
            });
            setDiscountError(false);
            return;
        }

        // Handle "custom" selection - allow custom discount input
        if (value.target.value === 'custom') {
            setDiscountId('custom');
            setSelectedDiscount(null);
            setPaymentDetails((prev) => ({
                ...prev,
                discountSource: DISCOUNT_SOURCE.PERCENTAGE,
            }));
            return;
        }
        if (!isDirty) {
            markSaved();
        }

        // Handle discount program selection
        const selectedDiscount = discountList.find((item) => item._id === value.target.value);
        setSelectedDiscount(selectedDiscount);
        const discountPercentage = Number(selectedDiscount?.discount) || 0;
        setDiscountId(value.target.value);

        // Calculate discount amount
        const discountValue = NumericEngine.divide(NumericEngine.multiply(paymentDetails?.subTotal, discountPercentage), 100);

        // Calculate taxable amount (subtotal - discount)
        const taxableAmount = NumericEngine.subtract(NumericEngine.add(paymentDetails.subTotal, feesAmount), discountValue);

        if (isNewTaxVersion) {
            setPaymentDetails({
                ...paymentDetails,
                discount: Number(discountValue),
                discountPercentage,
                discountSource: DISCOUNT_SOURCE.PERCENTAGE,
            });
            const isValidDiscount = paymentDetails?.subTotal === 0 || Number(discountValue) <= paymentDetails?.subTotal;
            setDiscountError(!isValidDiscount);
            return;
        }

        // Calculate tax on taxable amount
        const taxPercentage = Number(paymentDetails?.taxPercentage) || 0;
        const tax = taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';

        // Include fees in total calculation
        const total = NumericEngine.add(taxableAmount, tax);

        setPaymentDetails({
            ...paymentDetails,
            discount: Number(discountValue),
            discountPercentage,
            discountSource: DISCOUNT_SOURCE.PERCENTAGE,
            tax: Number(tax),
            total: Number(total),
        });

        // Validate discount
        const isValidDiscount = paymentDetails?.subTotal === 0 || Number(discountValue) <= paymentDetails?.subTotal;
        setDiscountError(!isValidDiscount);
    };

    return (
        <Grid item xs={12}>
            <Accordion
                expanded={accordianExpanded.includes(index)}
                style={{
                    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.10)',
                    borderRadius: '8px',
                    background: '#fff',
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <IconButton style={{ background: '#334FA9', padding: '0', width: '30px', height: '30px' }}>
                            <KeyboardArrowDown sx={{ color: '#fff', fontSize: '26px' }} />
                        </IconButton>
                    }
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className="accordianSummaryPadding"
                    style={{
                        minHeight: '80px',
                    }}
                    onClick={() => {
                        handleAccordianToggle(index);
                    }}
                >
                    <div className="accordianTitleWrapper">
                        <PaymentDetailSvg /> <Typography className="accordianTitle">Totals</Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails className="accordianDetailsWrapper">
                    <Grid container xs={12}>
                        <Grid container justifyContent={'space-between'} spacing={3}>
                            <Grid item xs={12} md={6} lg={5} xl={4.5}>
                                {/* SelectDiscountProgramField  */}
                                <Grid item xs={12}>
                                    <Stack gap={1}>
                                        <Typography className="formLabelTitle">Discount Type</Typography>

                                        <Select
                                            value={discountId || ''}
                                            onChange={(value) => {
                                                handleDiscountChange(value);
                                            }}
                                            sx={{
                                                height: '50px',
                                                boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                borderRadius: '8px',
                                                '& .MuiInputBase-root': {
                                                    border: 'none',
                                                    padding: '12px 12px 12px 0',
                                                    height: '50px',
                                                },
                                                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                                    borderRadius: '8px',
                                                },
                                            }}
                                        >
                                            <MenuItem value="none">
                                                <div>None</div>
                                            </MenuItem>
                                            <MenuItem value="custom">
                                                <div>Custom</div>
                                            </MenuItem>
                                            {discountList.map((item, index) => (
                                                <MenuItem key={index} value={item?._id}>
                                                    <Stack direction={'row'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                                                        <div> {item.programName}</div>
                                                        <Chip
                                                            sx={{
                                                                background: '#EBEBEB',
                                                                color: '#000',
                                                            }}
                                                            size="small"
                                                            label={`${item.discount}%`}
                                                        />
                                                    </Stack>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Stack>
                                </Grid>

                                {/* AddCustomDiscount  */}
                                {discountId === 'custom' && (
                                    <Grid item xs={12} mt={3}>
                                        <Stack gap={1}>
                                            <Typography className="formLabelTitle">Add Custom Discount</Typography>

                                            <Stack direction={'row'} justifyContent={'Center'} alignItems={'center'} gap={0} width={'100%'}>
                                                <TextField
                                                    fullWidth
                                                    value={pctInput}
                                                    disabled={
                                                        (!!discountId && discountId !== 'none' && discountId !== 'custom') ||
                                                        paymentDetails?.subTotal == '0' ||
                                                        !paymentDetails?.subTotal
                                                    }
                                                    color={discountError ? 'error' : 'primary'}
                                                    onFocus={(event) => {
                                                        const input = event.target;
                                                        setTimeout(() => {
                                                            input.setSelectionRange(input.value.length, input.value.length);
                                                        }, 0);
                                                        setPaymentDetails((prev) => ({
                                                            ...prev,
                                                            discountSource: DISCOUNT_SOURCE.PERCENTAGE,
                                                        }));
                                                    }}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        let numericValue = inputValue.replace(/[^\d.]/g, '');

                                                        if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
                                                            numericValue = numericValue.slice(1);
                                                        }

                                                        if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                                                            setPctInput(numericValue);
                                                            // Validate percentage
                                                            if (Number(numericValue) > 100) {
                                                                setDiscountError(true);
                                                                return false;
                                                            } else {
                                                                setDiscountError(false);
                                                            }

                                                            // Calculate discount amount from percentage
                                                            const discountPercentage = Number(numericValue) || 0; // "1." → 1
                                                            const subTotal = Number(paymentDetails?.subTotal) || 0;
                                                            const feesAmount = Number(paymentDetails?.fees) || 0;

                                                            const discountValue = NumericEngine.divide(Raw.mul(subTotal, discountPercentage), 100);

                                                            if (isNewTaxVersion) {
                                                                if (!isDirty) markSaved();
                                                                setPaymentDetails((prevPaymentDetails) => ({
                                                                    ...prevPaymentDetails,
                                                                    discountPercentage: numericValue,
                                                                    discount: Number(discountValue),
                                                                    discountSource: DISCOUNT_SOURCE.PERCENTAGE,
                                                                }));
                                                                return;
                                                            }

                                                            // Calculate taxable amount
                                                            const taxableAmount = NumericEngine.subtract(
                                                                NumericEngine.add(subTotal, feesAmount),
                                                                discountValue
                                                            );

                                                            // Calculate tax on taxable amount
                                                            const taxPercentage = Number(paymentDetails?.taxPercentage) || 0;
                                                            const tax =
                                                                taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';

                                                            const total = NumericEngine.add(taxableAmount, tax);
                                                            if (!isDirty) {
                                                                markSaved();
                                                            }
                                                            setPaymentDetails((prevPaymentDetails) => ({
                                                                ...prevPaymentDetails,
                                                                discountPercentage: numericValue,
                                                                discount: Number(discountValue),
                                                                discountSource: DISCOUNT_SOURCE.PERCENTAGE,
                                                                tax: Number(tax),
                                                                total: Number(total),
                                                            }));
                                                        }
                                                    }}
                                                    onKeyDown={handlePreventKeyDownDecimal}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment
                                                                style={{
                                                                    color:
                                                                        (!!discountId && discountId !== 'none' && discountId !== 'custom') ||
                                                                        paymentDetails?.subTotal == '0' ||
                                                                        !paymentDetails?.subTotal
                                                                            ? '#9E9E9E'
                                                                            : 'rgba(0,0,0,0.87)',
                                                                    fontSize: '16PX',
                                                                }}
                                                                position="end"
                                                                sx={{ fontSize: '16PX!important' }}
                                                            >
                                                                <span style={{ fontSize: '16PX' }}> %</span>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        height: '50px',
                                                        boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                        borderRadius: '8px',
                                                        '& .MuiInputBase-root': {
                                                            border: 'none',
                                                            padding: '12px 12px 12px 0',
                                                            height: '50px',
                                                            borderRadius: '8px',
                                                            fontSize: '16PX',
                                                        },
                                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                                            borderRadius: '8px',
                                                        },
                                                    }}
                                                />

                                                <div
                                                    style={{
                                                        width: '24px',
                                                        height: '4px',
                                                        background: '#E6E6E6',
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    color={discountError ? 'error' : 'primary'}
                                                    value={amtInput}
                                                    disabled={
                                                        (!!discountId && discountId !== 'none' && discountId !== 'custom') ||
                                                        paymentDetails?.subTotal == '0' ||
                                                        !paymentDetails?.subTotal
                                                    }
                                                    onFocus={(event) => {
                                                        const input = event.target;
                                                        setTimeout(() => {
                                                            input.setSelectionRange(input.value.length, input.value.length);
                                                        }, 0);
                                                        setPaymentDetails((prev) => ({
                                                            ...prev,
                                                            discountSource: DISCOUNT_SOURCE.AMOUNT,
                                                        }));
                                                    }}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        let numericValue = inputValue.replace(/[^\d.]/g, '');

                                                        if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
                                                            numericValue = numericValue.slice(1);
                                                        }

                                                        if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                                                            setAmtInput(numericValue);
                                                            // Calculate percentage based on amount
                                                            const discountAmount = Number(numericValue) || 0; // "1." → 1
                                                            const subTotal = Number(paymentDetails?.subTotal) || 0;
                                                            const feesAmount = Number(paymentDetails?.fees) || 0;

                                                            const discountPercentage =
                                                                subTotal > 0 ? NumericEngine.divide(Raw.mul(discountAmount, 100), subTotal) : '0';

                                                            // Validate percentage
                                                            if (Number(discountPercentage) > 100) {
                                                                setDiscountError(true);
                                                                return false;
                                                            } else {
                                                                setDiscountError(false);
                                                            }

                                                            if (isNewTaxVersion) {
                                                                if (!isDirty) markSaved();
                                                                setPaymentDetails((prevPaymentDetails) => ({
                                                                    ...prevPaymentDetails,
                                                                    discount: numericValue,
                                                                    discountPercentage: Number(discountPercentage),
                                                                    discountSource: DISCOUNT_SOURCE.AMOUNT,
                                                                }));
                                                                return;
                                                            }

                                                            // Calculate taxable amount
                                                            const taxableAmount = NumericEngine.subtract(
                                                                NumericEngine.add(subTotal, feesAmount),
                                                                discountAmount
                                                            );

                                                            // Calculate tax on taxable amount
                                                            const taxPercentage = Number(paymentDetails?.taxPercentage) || 0;
                                                            const tax =
                                                                taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';

                                                            const total = NumericEngine.add(taxableAmount, tax);

                                                            if (!isDirty) {
                                                                markSaved();
                                                            }

                                                            setPaymentDetails((prevPaymentDetails) => ({
                                                                ...prevPaymentDetails,
                                                                discount: numericValue,
                                                                tax: Number(tax),
                                                                discountPercentage: Number(discountPercentage),
                                                                discountSource: DISCOUNT_SOURCE.AMOUNT,
                                                                total: Number(total),
                                                            }));
                                                        }
                                                    }}
                                                    onKeyDown={handlePreventKeyDownDecimal}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment
                                                                style={{
                                                                    color:
                                                                        (!!discountId && discountId !== 'none' && discountId !== 'custom') ||
                                                                        paymentDetails?.subTotal == '0' ||
                                                                        !paymentDetails?.subTotal
                                                                            ? '#9E9E9E'
                                                                            : 'rgba(0,0,0,0.87)',
                                                                }}
                                                                position="end"
                                                            >
                                                                <span style={{ fontSize: '16PX' }}>$</span>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        height: '50px',
                                                        boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                        borderRadius: '8px',
                                                        '& .MuiInputBase-root': {
                                                            border: 'none',
                                                            padding: '12px 12px 12px 0',
                                                            height: '50px',
                                                            borderRadius: '8px',
                                                            fontSize: '16PX',
                                                        },
                                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                                            borderRadius: '8px',
                                                        },
                                                    }}
                                                />
                                            </Stack>
                                            {discountError && (
                                                <p
                                                    style={{
                                                        color: '#d32f2f',
                                                        fontSize: '0.75rem',
                                                        letterSpacing: '0.03333em',
                                                        lineHeight: '1.66',
                                                        marginTop: 0,
                                                        marginBottom: 0,
                                                    }}
                                                >
                                                    Discount cannot exceed 100% or be less than 0.
                                                </p>
                                            )}
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: {
                                        xs: 'block',
                                        md: 'none',
                                    },
                                }}
                            >
                                <Divider></Divider>
                            </Grid>

                            <Grid item xs={12} md={6} lg={7} xl={7.5}>
                                <Stack gap={1}>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography>Labour</Typography>
                                        <Typography>
                                            {paymentDetails?.labourTotal > 0
                                                ? NumericEngine.format(paymentDetails.labourTotal, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography>Parts</Typography>
                                        <Typography>
                                            {paymentDetails?.partsTotal > 0
                                                ? NumericEngine.format(paymentDetails.partsTotal, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography>Service Packages</Typography>
                                        <Typography>
                                            {paymentDetails?.packagesTotal > 0
                                                ? NumericEngine.format(paymentDetails.packagesTotal, {
                                                      trailingZeros: true,
                                                      formatCurrency: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    {structuredPaymentSummary && <Divider />}
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography>{structuredPaymentSummary ? 'Subtotal' : 'Sub Total'}</Typography>
                                        <Typography>
                                            {paymentDetails?.subTotal > 0
                                                ? NumericEngine.format(paymentDetails.subTotal, { trailingZeros: true, formatCurrency: true })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    {!hideFees && (
                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                            <Typography>Fees</Typography>
                                            <Typography>
                                                {paymentDetails?.fees > 0
                                                    ? NumericEngine.format(paymentDetails?.fees, {
                                                          formatCurrency: true,
                                                          trailingZeros: true,
                                                      })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                    )}

                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography>Discount</Typography>
                                        <Typography>
                                            {paymentDetails?.discountPercentage > 0 && `${formatNumberAsWhole(paymentDetails.discountPercentage)}% - `}

                                            {paymentDetails?.discount > 0
                                                ? NumericEngine.format(paymentDetails?.discount, {
                                                      formatCurrency: true,
                                                      trailingZeros: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                    {isNewTaxVersion ? (
                                        <MultiTaxRows
                                            taxesBreakdown={taxesBreakdown}
                                            setTaxesBreakdown={setTaxesBreakdown}
                                            documentType={documentType}
                                            taxPercentageInputRef={taxPercentageInputRef}
                                            lineTaxSummary={lineTaxSummary}
                                            onBulkToggle={onBulkTaxToggle}
                                        />
                                    ) : (
                                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                            <Typography>Tax</Typography>
                                            <Typography display={'flex'} alignItems={'center'}>
                                                <TextField
                                                    fullWidth
                                                    inputRef={taxPercentageInputRef}
                                                    value={paymentDetails?.taxPercentage}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end" style={{ marginLeft: '0' }}>
                                                                <span style={{ color: '#000' }}>%</span>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    error={isNaN(parseFloat(paymentDetails.taxPercentage))}
                                                    helperText={isNaN(parseFloat(paymentDetails.taxPercentage)) ? 'Tax is requried' : ''}
                                                    onChange={(e) => {
                                                        let inputValue = e.target.value;

                                                        if (inputValue.length > 1 && inputValue.startsWith('0') && /[1-9a-zA-Z]/.test(inputValue[1])) {
                                                            inputValue = inputValue.slice(1);
                                                        }

                                                        if (/^\d*\.?\d{0,3}$/.test(inputValue)) {
                                                            const taxPercentage = Number(inputValue) || 0;

                                                            if (taxPercentage < 100) {
                                                                const subTotal = Number(paymentDetails.subTotal) || 0;
                                                                const discount = Number(paymentDetails.discount) || 0;
                                                                const feesAmount = Number(paymentDetails?.fees) || 0;
                                                                const taxableAmount = NumericEngine.subtract(NumericEngine.add(subTotal, feesAmount), discount);
                                                                const tax =
                                                                    taxPercentage > 0
                                                                        ? NumericEngine.multiply(NumericEngine.divide(taxPercentage, 100), taxableAmount)
                                                                        : '0';
                                                                const total = NumericEngine.add(taxableAmount, tax);

                                                                setPaymentDetails({
                                                                    ...paymentDetails,
                                                                    taxPercentage: inputValue,
                                                                    tax: Number(tax),
                                                                    total: Number(total),
                                                                });
                                                            } else {
                                                                toast.error('Please enter a valid tax percentage (0-99)');
                                                            }
                                                        }
                                                    }}
                                                    sx={{
                                                        height: '36px',
                                                        width: '85px',
                                                        '& .MuiInputBase-root': {
                                                            height: '36px',
                                                            width: '85px',
                                                            borderRadius: '8px',
                                                            padding: '0 9.5px 0 0',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            width: '85px',
                                                            padding: '6px 9.5px',
                                                            paddingRight: '0',
                                                        },
                                                    }}
                                                />

                                                <span style={{ margin: '0 8px' }}>-</span>

                                                {paymentDetails?.tax > 0
                                                    ? NumericEngine.format(paymentDetails?.tax, {
                                                          formatCurrency: true,
                                                          trailingZeros: true,
                                                      })
                                                    : '$0.00'}
                                            </Typography>
                                        </Stack>
                                    )}
                                    <Divider />
                                    <Stack direction={'row'} justifyContent={'space-between'}>
                                        <Typography sx={{ fontWeight: structuredPaymentSummary ? 600 : undefined }}>Total</Typography>
                                        <Typography sx={{ fontWeight: structuredPaymentSummary ? 600 : undefined }}>
                                            {paymentDetails?.total > 0
                                                ? NumericEngine.format(paymentDetails?.total, {
                                                      formatCurrency: true,
                                                      trailingZeros: true,
                                                  })
                                                : '$0.00'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* PaymnetMethodSelect  */}
                            {/* <Grid item xs={7}>
                <Stack gap={1}>
                  <Typography className="formLabelTitle">
                    Payment Method{" "}
                    <span style={{ color: REQUIRED_RED }}>*</span>
                  </Typography>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    rules={{ required: "Payment method is required" }} // Validation rule
                    render={({ field }) => (
                      <Autocomplete
                        id="combo-box-demo"
                        options={PAYEMNT_METHODS}
                        getOptionLabel={(option) => option.value}
                        getOptionKey={(option) => option.key} // Optional, for consistency
                        value={
                          PAYEMNT_METHODS.find(
                            (item) => item.key === field.value?.key
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          const getTaxRate = customerInvoiceTaxes.find(
                            (method) => method.paymentMethod === newValue?.key
                          );
                          field.onChange(newValue);

                          const taxPercentage = getTaxRate?.taxRate || '0';

                          const feesAmount = paymentDetails?.fees || 0;

                          // Calculate taxable amount
                          const taxableAmount =
                            paymentDetails.subTotal +
                            feesAmount -
                            paymentDetails.discount;

                          const tax =
                            parseFloat(taxPercentage) > 0
                              ? (parseFloat(taxPercentage) / 100) * taxableAmount
                              : 0;

                          const total = taxableAmount + tax;

                          setPaymentDetails({
                            ...paymentDetails,
                            taxPercentage: taxPercentage, // Store as string
                            tax: tax,
                            total: total,
                          });
                        }}
                        ListboxProps={{
                          style: { maxHeight: "280px" },
                        }}
                        renderOption={(props, option) => (
                          <Box
                            key={props.key}
                            component="li"
                            sx={{
                              "& > img": { mr: 2, flexShrink: 0 },
                            }}
                            {...props}
                          >
                            {option.value}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.paymentMethod}
                            // helperText={
                            //   errors.paymentMethod
                            //     ? errors.paymentMethod.message
                            //     : ""
                            // }
                          />
                        )}
                        sx={{
                          height: "50px",
                          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                          "& .MuiInputBase-root": {
                            padding: "0 0 0 12px",
                            border: "none",
                            height: "50px",
                          },
                          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                            {
                              borderRadius: "8px",
                            },
                        }}
                      />
                    )}
                  />
                  {errors.paymentMethod && (
                    <FormHelperText error style={{ marginTop: "-3px" }}>
                      {errors.paymentMethod.message}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid> */}

                            {/* TaxesField */}
                            {/* <Grid item xs={12}>
                <Stack gap={1}>
                  <Typography className="formLabelTitle">
                    Taxes <span style={{ color: REQUIRED_RED }}>*</span>
                  </Typography>

                  <TextField
                    fullWidth
                    value={paymentDetails?.taxPercentage}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          style={{ color: "#9E9E9E" }}
                          position="end"
                        >
                          %
                        </InputAdornment>
                      ),
                    }}
                    error={isNaN(parseFloat(paymentDetails.taxPercentage))}
                    helperText={
                      isNaN(parseFloat(paymentDetails.taxPercentage))
                        ? "Tax is requried"
                        : ""
                    }
                    onChange={(e) => {
                      let inputValue = e.target.value;

                      // Remove leading zeros but keep decimal points
                      if (
                          inputValue.length > 1 &&
                          inputValue.startsWith('0') &&
                          /[1-9]/.test(inputValue[1])
                      ) {
                          inputValue = inputValue.slice(1);
                      }

                      // Allow only numbers and one decimal point with max 2 decimal places
                      if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
                          const taxPercentage = parseFloat(inputValue) || 0;
                          
                          if (taxPercentage <= 100) {
                              const { subTotal, discount } = paymentDetails;

                              // Include fees in total
                              const feesAmount = paymentDetails?.fees || 0;

                              // Calculate taxable amount
                              const taxableAmount = subTotal + feesAmount - discount;

                              // Calculate tax on taxable amount
                              let tax =
                                taxPercentage > 0
                                  ? (taxPercentage / 100) * taxableAmount
                                  : 0;
                              tax = formatNumberAsWhole(tax);

                              const total = taxableAmount + tax;

                              setPaymentDetails({
                                ...paymentDetails,
                                taxPercentage: inputValue, // Store as string
                                tax: tax,
                                total: total,
                              });
                          } else {
                              toast.error(
                                "Please enter a valid tax percentage (0-100)"
                              );
                          }
                      }
                    }}
                    sx={{
                      height: "50px",
                      boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.06)",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": {
                        border: "none",
                        padding: "12px 12px 12px 0",
                        height: "50px",
                        borderRadius: "8px",
                      },
                      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                        {
                          borderRadius: "8px",
                        },
                    }}
                  />
                </Stack>
              </Grid> */}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default PaymentDetailsAccrodian;
